import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Button from "@src/components/elements/input/Button";
import SellerCarousel from "@src/components/elements/sellerpage/SellerCarousel";
import SellerSubTabs from "@src/components/elements/sellerpage/SellerSubTabs";
import { UnorderedList } from "@src/components/elements/shared/ListComponents";

interface PackageCardProps {
  title: string;
  price: string;
  subtitle: string;
  features: string[];
  buttonLabel: string;
  link?: string;
  priceLabel?: string;
  theme?: "red" | "green" | "none";
}

const PackageCard: React.FC<PackageCardProps> = ({
  theme = "none",
  title,
  price,
  subtitle,
  features,
  buttonLabel,
  link = "",
  priceLabel,
}) => {
  const themes = {
    red: {
      borderStyle: "border-[3px] border-red-600 rounded-3xl",
      titleStyle: "bg-red-600 text-center text-gray-50 rounded-t-2xl",
      buttonStyle: Button.Variants.White,
    },
    green: {
      borderStyle: "border-[3px] border-green-600 rounded-3xl",
      titleStyle: "bg-green-600 text-center text-gray-50 rounded-t-2xl",
      buttonStyle: Button.Variants.Green,
    },
    none: {
      borderStyle: "border-[3px] border-gray-600 rounded-3xl",
      titleStyle: "bg-gray-600 text-center border-gray-100 text-gray-50 rounded-t-2xl",
      buttonStyle: Button.Variants.White,
    },
  };

  return (
    <div
      className={`flex h-auto w-full flex-col justify-between sm:size-[500px] sm:h-[500px] sm:max-w-[500px] ${themes[theme].borderStyle}`}
    >
      <div>
        <h1 className={`mb-4 md:mb-6 ${themes[theme].titleStyle}`}>{title}</h1>
        <div className="px-4 md:px-6">
          <div>
            <div className="flex flex-wrap items-end gap-x-1">
              <div className="text-3xl md:text-4xl">{price}</div>
              <div>{priceLabel}</div>
            </div>
            <div>{subtitle}</div>
          </div>
          <UnorderedList
            items={features}
            symbol="✅"
            className="flex flex-col gap-1 md:gap-1.5"
            gap="gap-x-1"
          />
        </div>
      </div>
      <div className="p-4 md:p-6">
        <Link to={link}>
          <Button variant={themes[theme].buttonStyle} size={Button.Sizes.Large}>
            {buttonLabel}
          </Button>
        </Link>
      </div>
    </div>
  );
};

export const SellerPackages = () => {
  const { t } = useTranslation();

  return (
    <>
      <PackageCard
        theme="red"
        price={t("main.marketplace.seller.packages.premium.price")}
        priceLabel={t("main.marketplace.seller.packages.taxLabel")}
        buttonLabel={t("main.marketplace.seller.packages.premium.action")}
        subtitle={t("main.marketplace.seller.packages.premium.subtitle")}
        title={t("main.marketplace.seller.packages.premium.title")}
        features={[
          t("main.marketplace.seller.packages.premium.benefits.text01"),
          t("main.marketplace.seller.packages.premium.benefits.text02"),
          t("main.marketplace.seller.packages.premium.benefits.text03"),
          t("main.marketplace.seller.packages.premium.benefits.text04"),
          t("main.marketplace.seller.packages.premium.benefits.text05"),
          t("main.marketplace.seller.packages.premium.benefits.text06"),
          t("main.marketplace.seller.packages.premium.benefits.text07"),
        ]}
      />
      <PackageCard
        link="/registrierung"
        theme="green"
        price={t("main.marketplace.seller.packages.free.price")}
        priceLabel=""
        buttonLabel={t("main.marketplace.seller.packages.free.action")}
        subtitle={t("main.marketplace.seller.packages.free.subtitle")}
        title={t("main.marketplace.seller.packages.free.title")}
        features={[
          t("main.marketplace.seller.packages.free.benefits.text01"),
          t("main.marketplace.seller.packages.free.benefits.text02"),
          t("main.marketplace.seller.packages.free.benefits.text03"),
          t("main.marketplace.seller.packages.free.benefits.text04"),
          t("main.marketplace.seller.packages.free.benefits.text05"),
          t("main.marketplace.seller.packages.free.benefits.text06"),
          t("main.marketplace.seller.packages.free.benefits.text07"),
        ]}
      />
      <PackageCard
        theme="none"
        price={t("main.marketplace.seller.packages.basic.price")}
        priceLabel={t("main.marketplace.seller.packages.taxLabel")}
        buttonLabel={t("main.marketplace.seller.packages.basic.action")}
        subtitle={t("main.marketplace.seller.packages.basic.subtitle")}
        title={t("main.marketplace.seller.packages.basic.title")}
        features={[
          t("main.marketplace.seller.packages.basic.benefits.text01"),
          t("main.marketplace.seller.packages.basic.benefits.text02"),
          t("main.marketplace.seller.packages.basic.benefits.text03"),
          t("main.marketplace.seller.packages.basic.benefits.text04"),
          t("main.marketplace.seller.packages.basic.benefits.text05"),
        ]}
      />
    </>
  );
};

export const SellerFooter = () => {
  const { t } = useTranslation();

  return (
    <footer className="mt-6 flex w-full flex-wrap justify-center gap-4 border-t px-4 py-3 text-xs text-gray-600 md:mt-8 md:gap-6 md:px-6 md:py-4">
      <Link to="/impressum" className="hover:underline">
        {t("seller.footer.Imprint")}
      </Link>
      <Link to="/agb" className="hover:underline">
        {t("seller.footer.T&C")}
      </Link>
      <Link to="/datenschutz" className="hover:underline">
        {t("seller.footer.Data Protection")}
      </Link>
      <Link
        to="/marketplace/offers/AGB/Customer_terms"
        className="hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t("seller.footer.Customer Terms")}
      </Link>
      <Link
        to="/marketplace/offers/AGB/MERCHANT_tearms"
        className="hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t("seller.footer.Seller Terms")}
      </Link>
      <Link
        to="/marketplace/offers/AGB/Merchant_Principles"
        className="hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t("seller.footer.Seller-Principles")}
      </Link>
      <Link to="/mehr" className="hover:underline">
        {t("seller.footer.More")}
      </Link>
    </footer>
  );
};

const SellerContainer = () => {
  const images = [
    "/images/seller_carousel/img01.jpg",
    "/images/seller_carousel/img02.jpg",
    "/images/seller_carousel/img03.jpg",
    "/images/seller_carousel/img04.jpg",
    "/images/seller_carousel/img05.jpg",
    "/images/seller_carousel/img06.jpg",
    "/images/seller_carousel/img07.jpg",
  ];
  const slides = [
    { type: "video" as const, src: "/images/seller_carousel/intro.mp4" },
    ...images.map(img => ({ type: "image" as const, src: img })),
  ];

  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-6 bg-gray-50 pb-4 md:gap-8 md:pb-6">
      <div className="sticky top-0 z-10 w-full bg-white shadow">
        <SellerSubTabs />
      </div>
      <section id="overview"></section>
      <div className="flex w-full flex-wrap justify-center gap-8 px-4 md:gap-12 md:px-6">
        <div className="max-w-[800px] text-xl md:text-2xl">
          <div className="mb-6 space-y-2 font-semibold md:mb-10 md:space-y-3">
            <div className="text-2xl md:text-3xl">{t("main.marketplace.seller.title.text01")}</div>
            <div className="text-2xl md:text-3xl">{t("main.marketplace.seller.title.text02")}</div>
            <div className="text-3xl font-extrabold text-red-500 md:text-[44px]">
              {t("main.marketplace.seller.title.text03")}
            </div>
          </div>

          <h2 className="mb-2 text-xl font-semibold md:mb-3 md:text-2xl">
            {t("main.marketplace.seller.title.text04")}
          </h2>

          <div className="mb-6 md:mb-10">{t("main.marketplace.seller.title.text05")}</div>

          <div className="mb-3 mt-4 text-lg font-semibold md:mb-4 md:mt-6 md:text-xl">
            {t("main.marketplace.seller.benefits.title")}
            {t("main.marketplace.seller.benefits.title1")}
          </div>

          <UnorderedList
            items={[
              <div className="mb-2" key="benefit-text8">
                {t("main.marketplace.seller.benefits.text08")}
              </div>,
              <div className="mb-2" key="benefit-text7">
                {t("main.marketplace.seller.benefits.text07")}
              </div>,
              <div className="mb-2" key="benefit-text6">
                {t("main.marketplace.seller.benefits.text06")}
              </div>,
              <div className="mb-2" key="benefit-text5">
                {t("main.marketplace.seller.benefits.text05")}
              </div>,
              <div className="mb-2" key="benefit-text4">
                {t("main.marketplace.seller.benefits.text04")}
              </div>,
              <div className="mb-8" key="benefit-text3">
                {t("main.marketplace.seller.benefits.text03")}
              </div>,
              <div className="mb-8" key="title-text2">
                <div className="mb-2 text-[44px] font-extrabold text-red-500">22.000+</div>
                <div className="text-[34px]">{t("main.marketplace.seller.benefits.text02.1")}</div>
                <div>{t("main.marketplace.seller.benefits.text02.2")}</div>
              </div>,
              <div className="mb-4" key="title-text1">
                <div className="mb-2 text-[44px] font-extrabold text-red-500">
                  {t("main.marketplace.seller.benefits.text01.1")}
                </div>
                <div>{t("main.marketplace.seller.benefits.text01.2")}</div>
              </div>,
              <div className="flex justify-center" key="cta-button">
                <Link to="/registrierung/unternehmen">
                  <Button fitWidth variant={Button.Variants.Register} size={Button.Sizes.Medium}>
                    {t("main.marketplace.seller.callToAction.text03")}
                  </Button>
                </Link>
              </div>,
            ]}
          />
        </div>

        <div
          className="relative h-[500px] w-full max-w-[700px] md:h-[750px]"
          style={{
            backgroundImage: "url('/images/europe_map.png')",
            backgroundSize: "cover",
            boxShadow: "0 0 30px 50px white inset",
          }}
        />
      </div>

      <section id="benefits" className="my-16"></section>
      <h1 className="mb-16 text-center text-3xl font-bold md:text-4xl">
        {t("seller.benefitsGrid.headline")}
      </h1>
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-12 md:grid-cols-2">
        {(() => {
          // Pre-fetch all translations to ensure i18next detects them
          const reachTitle = t("seller.benefitsGrid.reach.title");
          const reachPoints = t("seller.benefitsGrid.reach.points", {
            returnObjects: true,
          }) as string[];
          const growthTitle = t("seller.benefitsGrid.growth.title");
          const growthPoints = t("seller.benefitsGrid.growth.points", {
            returnObjects: true,
          }) as string[];
          const easyTitle = t("seller.benefitsGrid.easy.title");
          const easyPoints = t("seller.benefitsGrid.easy.points", {
            returnObjects: true,
          }) as string[];

          const sections = [
            { key: "reach", title: reachTitle, points: reachPoints },
            { key: "growth", title: growthTitle, points: growthPoints },
            { key: "easy", title: easyTitle, points: easyPoints },
          ];

          return sections.map(section => (
            <div key={section.key}>
              <h2 className="mb-2 text-lg font-semibold md:text-xl">{section.title}</h2>
              <ul className="ml-5 list-disc space-y-1 text-base">
                {section.points.map((point, idx) => (
                  <li key={`${section.key}-${idx}`}>{point}</li>
                ))}
              </ul>
            </div>
          ));
        })()}
        <div>
          <h2 className="mb-2 text-lg font-semibold md:text-xl">
            {t("seller.benefitsGrid.together.title")}
          </h2>
          <ul className="ml-5 list-disc space-y-1 text-base">
            {(t("seller.benefitsGrid.together.points", { returnObjects: true }) as string[]).map(
              (point, idx) => (
                <li key={"together-" + idx}>{point}</li>
              ),
            )}
          </ul>
        </div>
      </div>

      <SellerCarousel slides={slides} />

      <section id="packages"></section>
      <h1 className="mb-3 mt-6 px-4 text-center md:mb-4 md:mt-8 md:px-6">
        {t("main.marketplace.seller.packages.title")}
        {t("main.marketplace.seller.packages.title1")}
      </h1>
      <div className="mb-6 flex flex-wrap justify-center gap-6 px-4 md:mb-8 md:gap-8 md:px-6 lg:mb-16 lg:gap-24">
        <SellerPackages />
      </div>
      <Link
        className="my-4 block px-4 text-2xl text-red-600 underline md:my-6 md:px-6 md:text-3xl lg:my-10"
        to="/seller/conditions"
        onClick={() => {
          window.scrollTo(0, 0);
        }}
      >
        {t("main.marketplace.seller.provisionsLink")}
      </Link>

      <section id="howItWorks"></section>
      <h1 className="text-center">{t("main.marketplace.seller.faq.title")}</h1>
      <UnorderedList
        items={[
          <div className="flex flex-wrap gap-1" key="faq-register">
            <strong>{t("main.marketplace.seller.faq.text01.1")}</strong>
            <div>{t("main.marketplace.seller.faq.text01.2")}</div>
          </div>,
          <div className="flex flex-wrap gap-1" key="faq-offers">
            <strong>{t("main.marketplace.seller.faq.text02.1")}</strong>
            <div>{t("main.marketplace.seller.faq.text02.2")}</div>
          </div>,
          <div className="flex flex-wrap gap-1" key="faq-sell">
            <strong>{t("main.marketplace.seller.faq.text03.1")}</strong>
            <div>{t("main.marketplace.seller.faq.text03.2")}</div>
          </div>,
          <div className="flex flex-wrap gap-1" key="faq-support">
            <strong>{t("main.marketplace.seller.faq.text04.1")}</strong>
            <div>{t("main.marketplace.seller.faq.text04.2")}</div>
          </div>,
        ]}
      />

      <h1 className="mb-3 mt-6 px-4 md:mb-4 md:mt-8 md:px-6">
        {t("main.marketplace.seller.testimonials.title")}
      </h1>
      <div className="mb-6 flex flex-col gap-3 px-4 md:mb-8 md:gap-4 md:px-6 lg:mb-12">
        <div>
          <div>{t("main.marketplace.seller.testimonials.sample1.1")}</div>
          <div>{t("main.marketplace.seller.testimonials.sample1.2")}</div>
        </div>
        <div>
          <div>{t("main.marketplace.seller.testimonials.sample2.1")}</div>
          <div>{t("main.marketplace.seller.testimonials.sample2.2")}</div>
        </div>
      </div>
      <div className="mb-6 px-4 text-center md:mb-8 md:px-6 lg:mb-12">
        <h1 className="text-center">{t("main.marketplace.seller.callToAction.title")}</h1>
        <h1>{t("main.marketplace.seller.callToAction.text01")}</h1>
        <div>{t("main.marketplace.seller.callToAction.text02")}</div>
      </div>
      <Link to="/registrierung/unternehmen" className="mb-6 block px-4 md:mb-8 md:px-6 lg:mb-12">
        <Button fitWidth variant={Button.Variants.Register} size={Button.Sizes.Medium}>
          {t("main.marketplace.seller.callToAction.text03")}
        </Button>
      </Link>

      <SellerFooter />
    </div>
  );
};

export default SellerContainer;
